import dayjs from "dayjs";

const STATUS_ORDER = [
  { key: "REQUESTED", label: "Requested", color: "#F5A623" },
  { key: "SELECTED", label: "Selected", color: "#7B61FF" },
  { key: "SCHEDULED", label: "Scheduled", color: "#284889" },
  { key: "RESCHEDULED", label: "Rescheduled", color: "#00A3E0" },
  { key: "DONE", label: "Completed", color: "#0E7C66" },
  { key: "CANCELLED", label: "Cancelled", color: "#C0392B" },
  { key: "REJECTED", label: "Rejected", color: "#8E44AD" },
];

export function demoStatusKey(demo) {
  if (demo?.demoStatus === "DONE" || demo?.demoStatus === "COMPLETED") return "DONE";
  return demo?.demoStatus || "OTHER";
}

export function isCompletedDemo(demo) {
  return demoStatusKey(demo) === "DONE";
}

// Same rule as the rupee mark on the Completed status in Demo Management.
export function isClassAssigned(demo) {
  return isCompletedDemo(demo) && demo?.demofinalised === false;
}

function personName(person) {
  if (!person || typeof person !== "object") return "";
  return person.fullName || person.name || "";
}

function personPhone(person) {
  if (!person || person.phone == null || person.phone === "") return "";
  return String(person.phone);
}

function personEmail(person) {
  if (!person || typeof person !== "object" || !person.email) return "";
  return String(person.email);
}

function studentLabel(demo) {
  const names = [];
  if (Array.isArray(demo.students) && demo.students.length) {
    demo.students.forEach((student) => {
      names.push(student?.name || student?.fullName || "Unknown");
    });
  } else if (demo.tutorByStudent?.name) {
    names.push(demo.tutorByStudent.name);
  }
  return names.filter(Boolean).join(", ") || "--";
}

function studyLabel(demo) {
  const student = Array.isArray(demo.students) ? demo.students[0] : null;
  const className = student?.class?.name || "";
  const subject = student?.subjects?.[0]?.name || demo.subject?.[0]?.name || "";
  const board = student?.board?.name || "";
  return [className, subject, board].filter(Boolean).join(" · ") || "--";
}

function tutorOf(demo) {
  return demo.tutorByAdmin || demo.tutorId || null;
}

function demoWhen(demo) {
  return demo.demoDate || demo.preferedDate || demo.createdAt || null;
}

export async function loadReportDemos(api, range) {
  const rows = [];
  let expected;
  for (let page = 1; ; page += 1) {
    const response = await api.get("/demo", {
      params: { page, limit: 50, from: range.from, to: range.to },
    });
    const body = response.data;
    if (!body?.success || !Array.isArray(body.demos) || body.totalDemos == null) {
      throw new Error("Unable to load the complete report. Please try again.");
    }
    if (expected === undefined) expected = body.totalDemos;
    if (expected !== body.totalDemos) {
      throw new Error("Demos changed while loading. Please refresh the report.");
    }
    rows.push(...body.demos);
    const totalPages = body.totalPages || 0;
    if (!totalPages || page >= totalPages) break;
    if (!body.demos.length) throw new Error("Incomplete report. Please try again.");
  }
  if (rows.length !== expected || new Set(rows.map((row) => row._id)).size !== rows.length) {
    throw new Error("Demos changed while loading. Please refresh the report.");
  }
  return rows;
}

export function summarizeDemos(rows) {
  const completed = rows.filter(isCompletedDemo).length;
  const classAssigned = rows.filter(isClassAssigned).length;
  const byStatus = STATUS_ORDER.map((status) => ({
    ...status,
    count: rows.filter((row) => demoStatusKey(row) === status.key).length,
  }));
  const known = new Set(STATUS_ORDER.map((status) => status.key));
  const other = rows.filter((row) => !known.has(demoStatusKey(row))).length;
  if (other) byStatus.push({ key: "OTHER", label: "Other", color: "#657A99", count: other });
  return {
    total: rows.length,
    completed,
    classAssigned,
    conversion: completed ? (classAssigned / completed) * 100 : null,
    byStatus,
  };
}

export async function exportDemoReport({ rows, range }) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const summary = summarizeDemos(rows);
  const C = { navy: "#284889", sky: "#00A3E0", muted: "#657A99", light: "#F1F6FC" };
  const dateLabel = `${dayjs(range.from).format("DD MMM YYYY")} - ${dayjs(range.to).format("DD MMM YYYY")}`;
  const txt = (value, x, y, size = 10, color = C.navy, bold = false) => {
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(color);
    pdf.text(String(value), x, y);
  };
  const box = (x, y, w, h) => {
    pdf.setFillColor(C.light);
    pdf.roundedRect(x, y, w, h, 3, 3, "F");
  };
  const header = (title, subtitle) => {
    pdf.setFillColor(C.navy);
    pdf.rect(0, 0, 210, 43, "F");
    txt("EDUSHALA", 15, 14, 12, "#FFFFFF", true);
    txt("DEMO / REPORT", 145, 14, 8, "#BAC7E0");
    txt(title, 15, 28, 23, "#FFFFFF", true);
    txt(`${dateLabel}  |  ${subtitle}`, 15, 37, 8, "#D7E0F2");
  };
  const card = (x, y, w, label, value, note, color = C.navy) => {
    box(x, y, w, 30);
    txt(label, x + 4, y + 7, 8, C.muted, true);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    const size = Math.min(18, (18 * (w - 8)) / Math.max(pdf.getTextWidth(String(value)), 1));
    txt(value, x + 4, y + 18, size, color, true);
    txt(note, x + 4, y + 25, 7.5, C.muted);
  };
  const pieSlice = (cx, cy, radius, start, end) => {
    if (end <= start) return;
    if (end - start >= 2 * Math.PI - 1e-10) {
      pdf.circle(cx, cy, radius, "F");
      return;
    }
    pdf.moveTo(cx, cy);
    pdf.lineTo(cx + radius * Math.cos(start), cy + radius * Math.sin(start));
    const segments = Math.ceil((end - start) / (Math.PI / 2));
    const step = (end - start) / segments;
    for (let i = 0; i < segments; i += 1) {
      const a = start + i * step;
      const b = start + (i + 1) * step;
      const k = (4 / 3) * Math.tan((b - a) / 4);
      pdf.curveTo(
        cx + radius * (Math.cos(a) - k * Math.sin(a)),
        cy + radius * (Math.sin(a) + k * Math.cos(a)),
        cx + radius * (Math.cos(b) + k * Math.sin(b)),
        cy + radius * (Math.sin(b) - k * Math.cos(b)),
        cx + radius * Math.cos(b),
        cy + radius * Math.sin(b)
      );
    }
    pdf.close();
    pdf.fill();
  };

  const conversionLabel = summary.conversion == null ? "--" : `${summary.conversion.toFixed(1)}%`;
  header("Demo report", "Overview");
  card(15, 51, 86, "TOTAL DEMOS", String(summary.total), "Selected report period");
  card(109, 51, 86, "COMPLETED", String(summary.completed), "Status marked completed", C.sky);
  card(15, 86, 86, "CLASS ASSIGNED", String(summary.classAssigned), "Rupee mark on completed demos");
  card(109, 86, 86, "CLASS CONVERSION", conversionLabel, "Class assigned out of completed", C.sky);

  txt("Status split", 15, 128, 13, C.navy, true);
  txt("Class assigned means a completed demo that shows the rupee mark, so the class is running.", 15, 134, 8, C.muted);

  box(15, 140, 180, 78);
  txt("DEMOS BY STATUS", 20, 149, 9, C.navy, true);
  if (!summary.total) {
    txt("No demos in this period", 20, 166, 9, C.muted);
  } else {
    const slices = summary.byStatus.filter((status) => status.count > 0);
    let angle = -Math.PI / 2;
    slices.forEach((status) => {
      const end = angle + (2 * Math.PI * status.count) / summary.total;
      pdf.setFillColor(status.color);
      pieSlice(42, 182, 17, angle, end);
      angle = end;
    });
    slices.forEach((status, index) => {
      const column = index < 4 ? 0 : 1;
      const row = index % 4;
      const x = 72 + column * 58;
      const y = 162 + row * 12;
      pdf.setFillColor(status.color);
      pdf.circle(x, y - 1, 1.5, "F");
      txt(status.label, x + 4, y, 8, status.color, true);
      txt(`${status.count}  |  ${((status.count / summary.total) * 100).toFixed(1)}%`, x + 4, y + 4.5, 7.5, C.muted);
    });
  }

  const listed = [...rows].sort((a, b) => {
    const left = demoWhen(a) ? new Date(demoWhen(a)).getTime() : 0;
    const right = demoWhen(b) ? new Date(demoWhen(b)).getTime() : 0;
    return right - left;
  });

  let y = 0;
  const listPage = (continued) => {
    pdf.addPage();
    header("Parent & tutor", continued ? "Details continued" : "Demo details");
    card(15, 51, 56, "LIST ENTRIES", String(summary.total), "Selected report period");
    card(77, 51, 56, "COMPLETED", String(summary.completed), "Completed demos", C.sky);
    card(139, 51, 56, "CLASS ASSIGNED", String(summary.classAssigned), "Class is running");
    txt(continued ? "Demo list (continued)" : "Demo list", 15, 94, 13, C.navy, true);
    pdf.setFillColor(C.navy);
    pdf.rect(15, 100, 180, 8, "F");
    txt("DATE", 17, 105.2, 7.5, "#FFFFFF", true);
    txt("PARENT", 40, 105.2, 7.5, "#FFFFFF", true);
    txt("TUTOR", 108, 105.2, 7.5, "#FFFFFF", true);
    txt("STATUS", 158, 105.2, 7.5, "#FFFFFF", true);
    txt("CLASS", 178, 105.2, 7.5, "#FFFFFF", true);
    y = 108;
  };

  listPage(false);
  if (!listed.length) txt("No demos in this period.", 17, y + 8, 9, C.muted);

  listed.forEach((demo, index) => {
    const parent = demo.parentId && typeof demo.parentId === "object" ? demo.parentId : null;
    const tutor = tutorOf(demo);
    const parentLines = [
      personName(parent) || "Name unavailable",
      `Phone: ${personPhone(parent) || "--"}`,
      `Email: ${personEmail(parent) || "--"}`,
      `Student: ${studentLabel(demo)}`,
      studyLabel(demo),
    ];
    const tutorLines = tutor
      ? [
          personName(tutor) || "Name unavailable",
          `Phone: ${personPhone(tutor) || "--"}`,
          `Email: ${personEmail(tutor) || "--"}`,
        ]
      : ["Not assigned"];
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    const parentWrapped = parentLines.flatMap((line, lineIndex) =>
      pdf.splitTextToSize(line, lineIndex === 0 ? 64 : 64).map((part, partIndex) => ({
        text: part,
        bold: lineIndex === 0 && partIndex === 0,
        muted: lineIndex > 0,
      }))
    );
    const tutorWrapped = tutorLines.flatMap((line, lineIndex) =>
      pdf.splitTextToSize(line, 46).map((part, partIndex) => ({
        text: part,
        bold: lineIndex === 0 && partIndex === 0 && Boolean(tutor),
        muted: lineIndex > 0 || !tutor,
      }))
    );
    const height = Math.max(22, parentWrapped.length * 3.6 + 5, tutorWrapped.length * 3.6 + 5);
    if (y + height > 268) listPage(true);
    if (index % 2 === 0) {
      pdf.setFillColor(C.light);
      pdf.rect(15, y, 180, height, "F");
    }
    const when = demoWhen(demo);
    txt(when ? dayjs(when).format("DD MMM YY") : "--", 17, y + 5, 8);
    parentWrapped.forEach((line, lineIndex) => {
      txt(line.text, 40, y + 5 + lineIndex * 3.6, line.bold ? 8 : 7, line.muted ? C.muted : C.navy, line.bold);
    });
    tutorWrapped.forEach((line, lineIndex) => {
      txt(line.text, 108, y + 5 + lineIndex * 3.6, line.bold ? 8 : 7, line.muted ? C.muted : C.navy, line.bold);
    });
    const status = STATUS_ORDER.find((item) => item.key === demoStatusKey(demo));
    txt(status?.label || demo.demoStatus || "--", 158, y + 5, 7.5, status?.color || C.navy, true);
    const assigned = isClassAssigned(demo);
    txt(assigned ? "YES" : "--", 178, y + 5, 8, assigned ? C.sky : C.muted, assigned);
    y += height;
  });

  const pages = pdf.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    pdf.setDrawColor("#DCE3ED");
    pdf.line(15, 279, 195, 279);
    txt("Edushala | Demo report | Class assigned = rupee mark on a completed demo", 15, 286, 7.5, C.muted);
    txt(`${page} / ${pages}`, 184, 286, 8, C.muted);
  }
  const fromName = dayjs(range.from).format("YYYY-MM-DD");
  const toName = dayjs(range.to).format("YYYY-MM-DD");
  pdf.setProperties({ title: "Edushala Demo Report", author: "Edushala", subject: dateLabel });
  pdf.save(`Edushala-Demo-${fromName}-to-${toName}.pdf`);
  return pdf;
}
